/**
 * CohortParticipantView — Cohort detail page for enrolled participants.
 *
 * Tabs: Overview → My Activities → Participants → Mentors → Metrics
 */
import SpaceBetween from '@cloudscape-design/components/space-between';
import Box from '@cloudscape-design/components/box';
import Tabs from '@cloudscape-design/components/tabs';
import { Cohort } from '../../api/cohorts';
import { ProgramAdmin } from '../../api/programs';
import { useHashTab } from '../../hooks/useHashTab';
import { useQuery } from '@tanstack/react-query';
import { activitiesApi } from '../../api/activities';
import { queryKeys } from '../../lib/queryClient';
import { useAuthStore } from '../../stores/authStore';
import { useMyMentor, useUnenrollUser, useCohortParticipants } from '../../hooks/queries';
import { useNotifications } from '../../components/NotificationFlashbar';
import { CohortOverviewTab } from '../cohort-tabs/CohortOverviewTab';
import { MyActivitiesTab } from '../cohort-tabs/MyActivitiesTab';
import { ParticipantsTab } from '../cohort-tabs/ParticipantsTab';
import { MentorsTab } from '../cohort-tabs/MentorsTab';
import { useMentors } from '../../hooks/queries';

interface CohortParticipantViewProps {
  cohort: Cohort;
  programAdmins: ProgramAdmin[];
  programWikiUrl?: string;
  programName?: string;
  programOwnerId?: string;
  programOwnerName?: string;
}

export function CohortParticipantView({ cohort, programAdmins, programWikiUrl, programName, programOwnerId, programOwnerName }: CohortParticipantViewProps) {
  const [activeTabId, setActiveTabId] = useHashTab('overview');
  const user = useAuthStore((state) => state.user);
  const { showSuccess } = useNotifications();
  const unenrollMutation = useUnenrollUser();

  const { data: completions } = useQuery({
    queryKey: queryKeys.cohorts.userCompletions(cohort.cohortId, user?.userId ?? ''),
    queryFn: () => activitiesApi.getUserCompletions(cohort.cohortId, user!.userId),
    enabled: !!user?.userId,
  });

  const { data: activities } = useQuery({
    queryKey: queryKeys.activities.list(cohort.cohortId),
    queryFn: () => activitiesApi.list(cohort.cohortId),
  });

  const { data: mentor } = useMyMentor(cohort.cohortId);
  const { data: mentorsData } = useMentors(cohort.mentorsEnabled ? cohort.cohortId : undefined);
  const { data: participantsData } = useCohortParticipants(cohort.cohortId);
  const participantCount = participantsData?.participantCount;

  const completedCount = (completions || []).filter(
    (c: any) => c.status === 'completed' || c.status === 'pending_approval'
  ).length;
  const totalCount = activities?.length || 0;
  const progressPercent = totalCount > 0 ? Math.round((completedCount / totalCount) * 100) : 0;
  const pendingApprovals = (completions || []).filter((c: any) => c.status === 'pending_approval').length;

  const handleWithdraw = async () => {
    if (!user) return;
    await unenrollMutation.mutateAsync({ cohortId: cohort.cohortId, userId: user.userId });
    showSuccess('Withdrawn successfully', `You have successfully withdrawn from ${cohort.cohortName}.`);
  };

  const tabs = [
    {
      id: 'overview',
      label: 'Overview',
      content: (
        <CohortOverviewTab
          cohort={cohort}
          programAdmins={programAdmins}
          programWikiUrl={programWikiUrl}
          programOwnerId={programOwnerId}
          programOwnerName={programOwnerName}
          participantData={{
            progressPercent,
            completedCount,
            totalCount,
            mentor: mentor ?? null,
            pendingApprovals,
            onWithdraw: handleWithdraw,
          }}
          onNavigateToTab={setActiveTabId}
        />
      ),
    },
    {
      id: 'my-activities',
      label: `My Activities (${totalCount})`,
      content: (
        <MyActivitiesTab
          cohortId={cohort.cohortId}
          userId={user?.userId ?? ''}
          cohortStartDate={cohort.startDate}
          mentorsEnabled={cohort.mentorsEnabled}
          graduationApproval={cohort.graduationApproval}
        />
      ),
    },
    {
      id: 'participants',
      label: `Participants${participantCount !== undefined ? ` (${participantCount}${cohort.capacity ? `/${cohort.capacity}` : ''})` : ''}`,
      content: (
        <ParticipantsTab
          cohortId={cohort.cohortId}
          programId={cohort.programId}
          mentorsEnabled={cohort.mentorsEnabled}
        />
      ),
    },
    ...(cohort.mentorsEnabled ? [{
      id: 'mentors',
      label: `Mentors (${mentorsData?.length || 0})`,
      content: (
        <MentorsTab
          cohortId={cohort.cohortId}
          programId={cohort.programId}
          mentors={mentorsData || []}
          mentorRequests={[]}
          refetchMentors={() => {}}
          refetchMentorRequests={() => {}}
        />
      ),
    }] : []),
  ];

  return (
    <SpaceBetween size="m">
      <SpaceBetween size="xxs">
        <Box variant="h1">{programName ?? cohort.cohortName}</Box>
        {programName && <Box variant="h2" color="text-body-secondary">{cohort.cohortName}</Box>}
      </SpaceBetween>
      <Tabs activeTabId={activeTabId} onChange={({ detail }) => setActiveTabId(detail.activeTabId)} tabs={tabs} />
    </SpaceBetween>
  );
}
